import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Flex, Box, useColorMode } from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { getIsInDetachMode } from "src/duck/selectors";
import environment from "src/utils/environment";
import usePrevious from "src/hooks/usePrevious";
import { useControls } from "src/utils/createControls";
import withMediaCapturer from "src/modules/activity-log/enhancers/withMediaCapturer";
import { useParentStation } from "src/modules/parent-station/ParentStation/Context";
import { Activities } from "src/modules/activity-log/activity";
import { DetachMode } from "src/modules/detach-mode/DetachMode";
import EveryNoise from "src/modules/parent-station/components/EveryNoise";
import Settings from "src/modules/parent-station/components/Settings";
import stationSwitchProps from "src/modules/parent-station/lib/StationSwitchProps";
import LullabiesFlow from "src/modules/lullaby/LullabiesFlow";
import { isFreemium } from "src/modules/auth/duck/selectors";
import { useFrame } from "src/components/Frame/Context";
import IconAsButton from "src/components/IconAsButton";
import Disconnecting from "src/scenes/Disconnecting";
import FullScreen from "src/atoms/FullScreen";
import FreemiumVideo from "../../FreemiumVideo";
import ControllingPanel from "../../components/ControllingPanel";
import { BackToFocusedStation } from "./BackToFocusedStation";
import { StationMenu } from "./StationMenu";
import { Styles } from "./getMonitorStyle";
import Monitor from "./Monitor";

import { ReactComponent as MoreIcon } from "src/icons/more-horiz.svg";

const { isBrowser } = environment;

function Monitors({ mediaCapturer, lullabyPlayers }) {
  const { colorMode } = useColorMode();
  const parentStation = useParentStation();
  const frame = useFrame();
  const {
    babyStations,
    askToStopMonitoringForAll,
    stopMonitoringForAll,
    primaryStation,
    showTilesView,
    multiBabyManager,
    updatePrimaryStation,
    isBabyStationFamilyDevice,
    toggleTilesView,
    toggleMute,
    alert,
    muted,
    renderLowBatteryDialog,
    toggleLowBatteryDialog,
    togglePurchaseFlow,
    changeCamera,
    toggleMonitoringMode,
    lowBatteryDialogProps
  } = parentStation;
  const menu = useControls();
  const stationMenu = useControls();
  const [state, setState] = useState({
    lastNoiseBefore: null,
    lastMotionBefore: null,
    openedFlow: null,
    isActivityLogOpened: false,
    renderCameraList: false,
    shouldRotateCamera: true,
    renderDetachButton: false,
    renderVideoButton: false,
    isFading: false,
    opennedMenuId: null,
    frameStyle: null
  });
  const prevError = usePrevious(primaryStation?.error);
  const prevAlert = usePrevious(alert);
  const {
    openedFlow,
    isActivityLogOpened,
    renderCameraList,
    shouldRotateCamera,
    renderDetachButton,
    renderVideoButton,
    isFading,
    opennedMenuId,
    frameStyle
  } = state;
  const hasPrimaryStation =
    primaryStation && Object.keys(primaryStation).length !== 0;

  const animControlsRef = useRef({});
  const intervalRef = useRef(null);
  const syncTime = useRef(0);
  const componentWillUnmount = useRef(false);
  const stationMenuRefs = useRef([]);
  const isInDetachMode = useSelector(getIsInDetachMode);
  const isFreeVersion = useSelector(isFreemium);
  const numberOfStations = Object.keys(babyStations).length;

  const timeoutRef = useRef(null);

  const handleFade = useCallback(() => {
    if (timeoutRef.current) return;
    updateState({ isFading: true });
    timeoutRef.current = setTimeout(() => {
      updateState({ isFading: false });
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }, 1500);
  }, []);

  const detachMode = useMemo(() => {
    if (isBrowser) return null;
    return new DetachMode({
      handleFade: () => handleFade(),
      toggleDetachButton: (update) => {
        updateState({ renderDetachButton: update });
      },
      toggleVideoButton: (update) => {
        updateState({ renderVideoButton: update });
      }
    });
  }, [handleFade]);

  function updateState(update) {
    setState((prevState) => ({ ...prevState, ...update }));
  }

  function handleComponentUpdate() {
    const { error } = primaryStation;
    if (prevAlert !== alert) {
      updateState({ renderCameraList: false });
    }
    if (prevError === error) return;
    if (error) {
      updateState({
        openedFlow: null,
        isActivityLogOpened: false,
        renderCameraList: false
      });
    }
    if (alert && isInDetachMode) undetachWindow({ fade: false });
    if (renderLowBatteryDialog && isInDetachMode) {
      undetachWindow({ fade: false });
    }
  }

  const setShouldRotateCamera = useCallback(() => {
    const { senderStationConfiguration } = primaryStation;
    const cameraFacingList = {};
    senderStationConfiguration.availableCameras.forEach((camera) => {
      const { cameraFacing } = camera;
      if (!cameraFacingList[cameraFacing]) cameraFacingList[cameraFacing] = 0;
      cameraFacingList[cameraFacing] += 1;
      if (
        cameraFacingList[cameraFacing] >= 2 ||
        senderStationConfiguration.availableCameras.length > 2
      ) {
        updateState({ shouldRotateCamera: false });
      }
    });
  }, [primaryStation]);

  const handleTilesViewChange = useCallback(() => {
    if (showTilesView) {
      frame &&
        frame.update({
          content: null,
          containerCss: { position: "fixed", top: "0", bg: "transparent" }
        });
      Object.values(babyStations).forEach((station) => {
        if (station.resumeVideoMode && station.mode !== "video") {
          toggleMonitoringMode(station.jid);
        }
      });
    } else {
      animControlsRef.current = {};
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [showTilesView, babyStations, frame, toggleMonitoringMode]);

  const handleVideoResumption = useCallback(() => {
    if (primaryStation.resumeVideoMode && primaryStation.mode !== "video") {
      toggleMonitoringMode(primaryStation.jid);
    }
  }, [primaryStation, toggleMonitoringMode]);

  const undetachWindow = useCallback(
    async ({ fade } = { fade: true }) => {
      await detachMode.handleUndetach(fade);
    },
    [detachMode]
  );

  const updateFrameStyle = useCallback(({ switcherProps }) => {
    updateState({ frameStyle: switcherProps });
  }, []);

  const onFreeVideoExpiration = useCallback(() => {
    if (isInDetachMode) undetachWindow();
    toggleMonitoringMode(primaryStation.jid);
    togglePurchaseFlow("unlimitedMonitoring");
  }, [
    isInDetachMode,
    undetachWindow,
    toggleMonitoringMode,
    primaryStation.jid,
    togglePurchaseFlow
  ]);

  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (environment.isDev) toggleMute();
    stationSwitchProps.on("switcher-props-update", updateFrameStyle);
    FreemiumVideo.on("timer-end", onFreeVideoExpiration);
    return () => {
      stationSwitchProps.removeListener(
        "switcher-props-update",
        updateFrameStyle
      );
      FreemiumVideo.off("timer-end", onFreeVideoExpiration);
    };
  }, [onFreeVideoExpiration, toggleMute, updateFrameStyle]);

  useEffect(() => {
    handleComponentUpdate();
  });

  useEffect(() => {
    setShouldRotateCamera();
    handleVideoResumption();
  }, [primaryStation, setShouldRotateCamera, handleVideoResumption]);

  useEffect(() => {
    handleTilesViewChange();
  }, [showTilesView, handleTilesViewChange]);

  useEffect(() => {
    return () => {
      if (!frame) return;
      frame.setDefaults();
    };
  }, [frame]);

  useEffect(() => {
    return () => {
      componentWillUnmount.current = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (componentWillUnmount.current) {
        if (isInDetachMode) undetachWindow({ fade: false });
        detachMode?.clearListeners();
      }
    };
  }, [detachMode, isInDetachMode, undetachWindow]);

  function handleStationSelect(selected) {
    // turn off video mode for other stations
    Object.values(babyStations)
      .filter((filtered) => filtered.jid !== selected.jid)
      .forEach((station) => {
        if (station.mode === "video") {
          toggleMonitoringMode(station.jid, true);
        }
      });
    updatePrimaryStation(selected);
    multiBabyManager.open();
    toggleTilesView();
  }

  function detachWindow() {
    if (isActivityLogOpened) toggleActivityLog();
    detachMode?.handleDetach();
  }

  const toggleActivityLog = () =>
    updateState({ isActivityLogOpened: !isActivityLogOpened });
  const openCameraList = () => updateState({ renderCameraList: true });
  const closeCameraList = () => updateState({ renderCameraList: false });
  const closeFlow = () => updateState({ openedFlow: null });
  const openSettings = () => {
    menu.close();
    updateState({ openedFlow: Settings });
  };
  const openEveryNoise = () => {
    menu.close();
    updateState({ openedFlow: EveryNoise });
  };
  const openLullabyPlayer = () => {
    menu.close();
    updateState({ openedFlow: LullabiesFlow });
  };

  function handleAnimationSync(controls) {
    const { id, ...props } = controls;
    const hasProps = props && Object.keys(props).length !== 0;

    if (!hasProps) delete animControlsRef.current[id];
    if (hasProps) animControlsRef.current[id] = controls;

    if (Object.keys(animControlsRef.current).length === numberOfStations) {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(() => {
        if (syncTime.current === 210) syncTime.current = 0;
        Object.values(animControlsRef.current).forEach((anim) => {
          anim.goToAndStop(syncTime.current, true);
        });
        syncTime.current += 1;
      }, 16.6);
    }
  }

  return (
    <>
      <FullScreen
        ignoreFrame={
          showTilesView ||
          (primaryStation.mode === "video" && !primaryStation.error)
        }
      >
        {!showTilesView &&
          hasPrimaryStation &&
          primaryStation.status !== "disconnecting" && (
            <Flex
              justifyContent="start"
              alignItems="center"
              flexDirection="column"
              width="100%"
              height="100%"
            >
              <ControllingPanel
                {...primaryStation}
                menu={menu}
                changeCamera={changeCamera}
                mediaCapturer={mediaCapturer}
                lullabyPlayer={lullabyPlayers?.[primaryStation.lullabyPlayerId]}
                stopMonitoring={
                  numberOfStations > 1
                    ? askToStopMonitoringForAll
                    : stopMonitoringForAll
                }
                isInDetachMode={isInDetachMode}
                enableDetach={numberOfStations === 1 && !isBrowser}
                togglePurchaseFlow={togglePurchaseFlow}
                shouldRotateCamera={shouldRotateCamera}
                isActivityLogOpened={isActivityLogOpened}
                isBabyStationFamilyDevice={isBabyStationFamilyDevice}
                toggleMute={toggleMute}
                openSettings={openSettings}
                detachWindow={detachWindow}
                openCameraList={openCameraList}
                openEveryNoise={openEveryNoise}
                toggleActivityLog={toggleActivityLog}
                openLullabyPlayer={openLullabyPlayer}
                frameStyle={frameStyle}
                muted={muted}
              />
              <Monitor
                muted={muted}
                isFading={isFading}
                openedFlow={openedFlow}
                changeCamera={changeCamera}
                babyStation={primaryStation}
                isInTileView={showTilesView}
                lullabyPlayer={lullabyPlayers?.[primaryStation.lullabyPlayerId]}
                isInDetachMode={isInDetachMode}
                renderCameraList={renderCameraList}
                togglePurchaseFlow={togglePurchaseFlow}
                renderDetachButton={renderDetachButton}
                isActivityLogOpened={isActivityLogOpened}
                detailNameInDialogs={numberOfStations > 1}
                renderLowBatteryDialog={renderLowBatteryDialog}
                toggleLowBatteryDialog={toggleLowBatteryDialog}
                lowBatteryDialogProps={lowBatteryDialogProps}
                renderVideoButton={renderVideoButton && !isFreeVersion}
                isFreeVersion={isFreeVersion}
                closeFlow={closeFlow}
                undetachWindow={undetachWindow}
                closeCameraList={closeCameraList}
                openLullabyPlayer={openLullabyPlayer}
                toggleActivityLog={toggleActivityLog}
                toggleMonitoringMode={toggleMonitoringMode}
              />
            </Flex>
          )}
        {showTilesView && (
          <Flex
            width="100%"
            height="100%"
            flexWrap="wrap"
            justifyContent="flex-end"
          >
            <BackToFocusedStation
              station={primaryStation}
              onClick={() => handleStationSelect(primaryStation)}
            />
            {Object.values(babyStations).map((babyStation, i) => {
              const { style, size, avatarNamePosition } =
                numberOfStations > 1 ? Styles[numberOfStations][i] : "";
              const countAsDarkMode =
                babyStation.error ||
                colorMode === "dark" ||
                babyStation.activity === Activities.AWAKE ||
                babyStation.activity === Activities.MOTION;
              return (
                <Box
                  key={babyStation.jid}
                  position="relative"
                  width="50%"
                  transition="all 0.3s"
                  {...style}
                >
                  <StationMenu
                    mode={babyStation.mode}
                    menuRef={
                      <Box ref={(el) => (stationMenuRefs.current[i] = el)}>
                        <IconAsButton
                          onClick={() => {
                            updateState({ opennedMenuId: i });
                            stationMenu.open();
                          }}
                          fill={
                            babyStation.mode === "video"
                              ? "white"
                              : countAsDarkMode
                              ? "white70"
                              : "black54"
                          }
                          icon={MoreIcon}
                          size="20px"
                        />
                      </Box>
                    }
                    isOpen={stationMenu.isOpen && opennedMenuId === i}
                    onClose={() => {
                      updateState({ opennedMenuId: null });
                      stationMenu.close();
                    }}
                    toggleVideoMode={() => {
                      toggleMonitoringMode(babyStation.jid);
                    }}
                    handleStationSelect={() => handleStationSelect(babyStation)}
                  />

                  <Flex
                    justifyContent="center"
                    alignItems="center"
                    width="100%"
                    height="100%"
                  >
                    <Monitor
                      lullabyPlayer={
                        lullabyPlayers?.[babyStation.lullabyPlayerId]
                      }
                      muted={muted}
                      babyStation={babyStation}
                      renderLowBatteryDialog={renderLowBatteryDialog}
                      toggleLowBatteryDialog={toggleLowBatteryDialog}
                      lowBatteryDialogProps={lowBatteryDialogProps}
                      detailNameInDialogs={numberOfStations > 1}
                      isInTileView={showTilesView}
                      showSmallAvatar={
                        numberOfStations === 4 ||
                        (numberOfStations === 3 && i !== 0)
                      }
                      avatarSize={size}
                      avatarNamePosition={avatarNamePosition}
                      setAnimationControls={(controls) =>
                        handleAnimationSync({ id: i, ...controls })
                      }
                    />
                  </Flex>
                </Box>
              );
            })}
          </Flex>
        )}
      </FullScreen>
      {primaryStation.status === "disconnecting" && <Disconnecting />}
    </>
  );
}

export default withMediaCapturer(Monitors);
